import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Box, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MDTypography } from '../../components/MDTypography';
import { MDButton } from '../../components/MDButton';
import { MDBadge } from '../../components/MDBadge';
import AddIcon from '@material-ui/icons/Add';
import {
  Task,
  TaskFilters,
  CreateTaskInput,
  TaskStatus,
  TaskPriority,
  TaskDetail,
} from '../../types/interfaces/task.types';
import { getTasks, createTask, deleteTask } from '../../services/taskService';
import { Severity } from '../../types';
import useSnackbar from '../../hooks/useSnackbar';
import {
  FFTable,
  FFTableColumn,
  FFTableFilterValue,
} from '../../components/FFTable';

// Components
import TaskDetailsPanel from './components/TaskDetailsPanel';
import CreateTaskDialog from './components/CreateTaskDialog';
import { useAuthContext } from 'contexts/authContext';
import { useTasks } from 'hooks/useTasks';
import { logger } from 'services/logger';
import { isAxiosError } from 'axios';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  actionButton: {
    minWidth: 80,
    padding: theme.spacing(0.5, 1),
    fontSize: '0.75rem',
  },
}));

type TasksPageData = {
  entities: Task[];
  isLoading: boolean;
  error: null | Error;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

type QueryState = {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters: TaskFilters;
};

type BadgeColor =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'light'
  | 'dark';

const formatEnumText = (text: string) => {
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getStatusColor = (status: TaskStatus): BadgeColor => {
  switch (status) {
    case TaskStatus.OPEN:
      return 'info';
    case TaskStatus.IN_PROGRESS:
      return 'warning';
    case TaskStatus.RESOLVED:
      return 'success';
    case TaskStatus.CLOSED:
      return 'dark';
    default:
      return 'secondary'; // WAITING and others
  }
};

const getPriorityColor = (priority: TaskPriority): BadgeColor => {
  switch (priority) {
    case TaskPriority.LOW:
      return 'success';
    case TaskPriority.NORMAL:
      return 'info';
    case TaskPriority.HIGH:
      return 'warning';
    case TaskPriority.URGENT:
      return 'error';
    default:
      return 'info';
  }
};

export const TasksPage: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const { user } = useAuthContext();

  const [pageData, setPageData] = useState<TasksPageData>({
    entities: [],
    isLoading: true,
    error: null,
    pagination: {
      page: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0,
    },
  });

  const [queryState, setQueryState] = useState<QueryState>({
    page: 1,
    pageSize: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    filters: {},
  });

  const [activeFilters, setActiveFilters] = useState<FFTableFilterValue[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [hideResolvedClosed, setHideResolvedClosed] = useState(false);
  const { tenants } = useTasks();

  // Columns Definition
  const columns: FFTableColumn<Task>[] = [
    {
      id: 'createdAt',
      label: t('Created At'),
      type: 'text',
      sortable: true,
      renderCell: (value) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'topic',
      label: t('Topic'),
      type: 'text',
      renderCell: (value, row) => row?.topic?.name || 'N/A',
    },
    {
      id: 'status',
      label: t('Status'),
      type: 'text',
      sortable: true,
      filterable: true,
      // renderCell: (value) => <span>{formatEnumText(value)}</span>,
      renderCell: (value) => (
        <MDBadge
          color={getStatusColor(value as TaskStatus)}
          variant="gradient"
          size="sm"
          badgeContent={formatEnumText(value)}
          container
        />
      ),
    },
    // {
    //   id: 'priority',
    //   label: t('Priority'),
    //   type: 'text',
    //   sortable: true,
    //   filterable: true,
    //   renderCell: (value) => {
    //     const color = getPriorityColor(value as TaskPriority);
    //     if (color === 'info') {
    //       return <span>{formatEnumText(value)}</span>;
    //     }
    //     return (
    //       <MDBadge
    //         color={color}
    //         variant="gradient"
    //         size="sm"
    //         badgeContent={formatEnumText(value)}
    //         container
    //       />
    //     );
    //   },
    // },
    {
      id: 'dueDate',
      label: t('Due Date'),
      type: 'text',
      sortable: true,
      renderCell: (value) =>
        value ? new Date(value).toLocaleDateString() : '-',
    },
  ];

  if (user?.userActor !== 'CARRIER' && tenants && tenants.tenants.length > 0) {
    let colIndex = 1;
    if (user?.userActor === 'FAIRFARE') {
      columns.splice(colIndex, 0, {
        id: 'caseId',
        label: t('Case ID'),
        type: 'text',
      });
      colIndex++;
    }
    columns.splice(colIndex, 0, {
      id: 'policyNumber',
      label: t('Policy Number'),
      type: 'text',
    });
    colIndex++;

    columns.splice(colIndex, 0, {
      id: 'carrier',
      label: t('Carrier'),
      type: 'text',
      renderCell: (value, row) =>
        row.responsibleTenantId === 1
          ? 'Fairfare'
          : tenants.tenants.find((t) => t.id === row.responsibleTenantId)
              ?.name || 'Unknown',
    });
  } else {
    columns.splice(1, 0, {
      id: 'caseId',
      label: t('Case ID'),
      type: 'text',
    });
  }

  const fetchDataWithQuery = useCallback(
    async (query: QueryState) => {
      try {
        setPageData((prev) => ({ ...prev, isLoading: true }));

        const response = await getTasks(
          query.filters,
          query.page,
          query.pageSize,
          query.sortBy,
          query.sortOrder
        );

        const total = response.pagination.total || 0;
        const pageSize = response.pagination.pageSize || 20;

        setPageData({
          entities: response.tasks,
          isLoading: false,
          error: null,
          pagination: {
            page: response.pagination.page,
            pageSize: pageSize,
            total: total,
            totalPages: pageSize > 0 ? Math.ceil(total / pageSize) : 0,
          },
        });

        setQueryState(query);
      } catch (error) {
        const e = error as Error;
        setPageData((prev) => ({
          ...prev,
          isLoading: false,
          error: e,
        }));
        showSnackbar('Failed to load tasks', Severity.error);
      }
    },
    [showSnackbar]
  );

  // Initial fetch
  useEffect(() => {
    fetchDataWithQuery(queryState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handlePageChange = useCallback(
    (newPage: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: newPage,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: 1,
        pageSize: newPageSize,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const handleSortChange = useCallback(
    (orderBy: string, order: 'asc' | 'desc') => {
      const newQuery: QueryState = {
        ...queryState,
        page: 1,
        sortBy: orderBy,
        sortOrder: order,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const handleFilterChange = useCallback(
    (filters: FFTableFilterValue[]) => {
      setActiveFilters(filters);

      // Map FFTable filters to TaskFilters
      const newFilters: TaskFilters = {
        hideResolvedClosed,
      };

      filters.forEach((filter) => {
        if (filter.columnId === 'status') {
          newFilters.status = filter.value as TaskStatus;
        } else if (filter.columnId === 'priority') {
          newFilters.priority = filter.value as TaskPriority;
        } else if (filter.columnId === 'title' || filter.columnId === 'id') {
          newFilters.search = filter.value as string;
        }
      });

      const newQuery: QueryState = {
        ...queryState,
        page: 1,
        filters: newFilters,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery, hideResolvedClosed]
  );

  const handleTaskDelete = useCallback(
    async (task: TaskDetail) => {
      try {
        await deleteTask(task.id);
      } catch (error) {
        if (isAxiosError(error)) {
          logger.error('Failed to delete task', {
            status: error.response?.status.toString(),
            data: error.response?.data,
          });
        }
      }
      fetchDataWithQuery(queryState);
      setSelectedTaskId(null);
    },
    [queryState, fetchDataWithQuery]
  );

  const toggleHideResolvedClosed = useCallback(() => {
    const newValue = !hideResolvedClosed;
    setHideResolvedClosed(newValue);

    const newFilters: TaskFilters = {
      ...queryState.filters,
      hideResolvedClosed: newValue,
    };

    const newQuery: QueryState = {
      ...queryState,
      page: 1,
      filters: newFilters,
    };
    fetchDataWithQuery(newQuery);
  }, [hideResolvedClosed, queryState, fetchDataWithQuery]);

  const handleCreateTask = useCallback(
    async (input: CreateTaskInput) => {
      try {
        const newTask = await createTask(input, user!.userActor!);
        if (newTask) {
          setIsCreateDialogOpen(false);
          showSnackbar('Task created successfully', Severity.success);
          // Refresh list
          fetchDataWithQuery(queryState);
          // Optionally open the new task
          setSelectedTaskId(newTask.id);
        }
      } catch (error) {
        showSnackbar('Failed to create task', Severity.error);
      }
    },
    [queryState, fetchDataWithQuery, showSnackbar]
  );

  const handleTaskUpdated = useCallback(() => {
    fetchDataWithQuery(queryState);
  }, [queryState, fetchDataWithQuery]);

  const handleRowClick = useCallback((row: Task) => {
    setSelectedTaskId(row.id);
  }, []);

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Box className={classes.header}>
        <MDTypography variant="h4" component="h1">
          {t('Tasks Management')}
        </MDTypography>
        <Box display="flex" alignItems="center">
          <MDButton
            variant="outlined"
            color="info"
            onClick={toggleHideResolvedClosed}
            style={{ marginRight: '16px' }}
          >
            {hideResolvedClosed
              ? t('Show Resolved/Closed')
              : t('Hide Resolved/Closed')}
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateDialogOpen(true)}
          >
            {t('Create Task')}
          </MDButton>
        </Box>
      </Box>

      <FFTable
        columns={columns}
        rows={pageData.entities}
        serverSide={true}
        loading={pageData.isLoading}
        error={pageData.error}
        // Pagination
        pagination={{
          page: pageData.pagination.page,
          pageSize: pageData.pagination.pageSize,
          total: pageData.pagination.total,
          totalPages: pageData.pagination.totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [20, 50, 100],
        }}
        // Sorting
        initialSort={{
          orderBy: queryState.sortBy || 'createdAt',
          order: queryState.sortOrder || 'desc',
        }}
        onRowClick={handleRowClick}
        onSortChange={handleSortChange}
        // Filtering
        initialFilters={activeFilters}
        onFilterChange={handleFilterChange}
        filterColumnsOptions={['id', 'title', 'status', 'priority']}
        rowKey="id"
        emptyStateMessage={t('No tasks found')}
        errorStateMessage={t('Error loading tasks')}
        loadingStateMessage={t('Loading tasks...')}
      />

      <TaskDetailsPanel
        taskId={selectedTaskId}
        open={!!selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        onTaskUpdated={handleTaskUpdated}
        onTaskDeleted={handleTaskDelete}
      />

      <CreateTaskDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreate={handleCreateTask}
      />
    </Container>
  );
};
